import { Button } from "@/components/ui/button";
import { Phone, ShieldCheck, Star, Users, ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/products";

export default function Hero({ onQuoteClick }) {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 overflow-hidden bg-gradient-to-b from-[#fef9ec] via-white to-white"
    >
      <div
        className="brand-blob"
        style={{ background: "#f5b942", top: "-10%", left: "-10%" }}
      />
      <div
        className="brand-blob"
        style={{ background: "#1e3a8a", bottom: "-20%", right: "-10%", opacity: 0.18 }}
      />

      <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span data-testid="hero-eyebrow" className="eyebrow">
              <ShieldCheck className="w-3.5 h-3.5" /> IRDAI Registered Insurance Broker
            </span>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Insurance made <span className="text-[#1e3a8a]">simple,</span>
              <br /> protection made{" "}
              <span className="relative inline-block">
                <span className="relative z-10">personal.</span>
                <span className="absolute inset-x-0 -bottom-1 h-3 bg-[#f5b942]/70 -z-0 rounded"></span>
              </span>
            </h1>
            <p className="font-hindi mt-5 text-lg sm:text-xl text-[#1e3a8a] font-semibold">
              {BRAND.tagline_hi}
            </p>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
              Compare and buy the right cover for your car, family, truck,
              tractor or next trip — at the lowest premium, with hand-holding
              support from real insurance experts.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                data-testid="hero-primary-cta"
                onClick={onQuoteClick}
                className="bg-[#f5b942] hover:bg-[#d99b28] text-slate-900 font-bold rounded-full px-7 h-12 shadow-md hover:shadow-xl transition-all hover:-translate-y-0.5"
              >
                Get Free Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <a
                href={`tel:+91${BRAND.phones[0]}`}
                data-testid="hero-call-cta"
                className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-white border-2 border-slate-200 hover:border-[#1e3a8a] text-slate-900 font-bold transition-all"
              >
                <Phone className="w-4 h-4" /> Call +91 {BRAND.phones[0]}
              </a>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
              <Stat icon={Users} value="10,000+" label="Happy Clients" />
              <Stat icon={ShieldCheck} value="6+" label="Insurance Products" />
              <Stat icon={Star} value="4.9/5" label="Client Rating" />
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <img
                src="https://images.unsplash.com/photo-1659352790654-058e9077a4f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGluZGlhbiUyMGZhbWlseSUyMHNtaWxpbmd8ZW58MHx8fHwxNzgyNTQzODQ1fDA&ixlib=rb-4.1.0&q=85"
                alt="Protected Indian family"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a8a]/40 via-transparent to-transparent" />
            </div>

            <div className="absolute -bottom-6 -left-4 md:-left-8 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-slate-100 animate-float">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <div className="text-xs text-slate-500">Claim Settlement</div>
                <div className="font-bold text-slate-900 text-lg">98.7%</div>
              </div>
            </div>

            <div className="absolute -top-4 -right-2 md:-right-6 bg-[#1e3a8a] text-white rounded-2xl shadow-xl px-4 py-3 max-w-[220px]">
              <div className="text-xs opacity-80 uppercase tracking-wider">
                Family Cover
              </div>
              <div className="font-bold text-lg leading-tight">
                Starting ₹6/day
              </div>
              <div className="text-[10px] opacity-70 mt-1">*Terms apply</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-9 h-9 rounded-lg bg-[#1e3a8a]/10 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#1e3a8a]" />
      </div>
      <div>
        <div className="font-display font-extrabold text-slate-900 text-lg leading-none">
          {value}
        </div>
        <div className="text-xs text-slate-500 mt-1">{label}</div>
      </div>
    </div>
  );
}
